import React, { useState, useEffect, useCallback } from 'react';
import SidePanel from '../util/SidePanel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import { renderIcon } from '../util/IconPicker';
import { genderLabel } from '../util/localeHelpers';
import CustomFieldValues from '../util/CustomFieldValues';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editContact?: any;
}

function Section({ label, children, defaultOpen = true }: {
  label: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-[14px]">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between pb-[5px] group"
        style={{ borderBottom: 'var(--border-input)' }}>
        <span className="text-[10px] uppercase tracking-wider opacity-40 group-hover:opacity-70 transition-opacity text-[var(--text-color)]">
          {label}
        </span>
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
          className="opacity-30 group-hover:opacity-60 transition-opacity flex-shrink-0 text-[var(--text-color)]"
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }}>
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open && <div className="pt-[8px]">{children}</div>}
    </div>
  );
}

export default function AddCrmContact({ open, onClose, onSaved, editContact }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [contactTypes, setContactTypes] = useState<any[]>([]);
  const [organisations, setOrganisations] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);

  const [contactTypeId, setContactTypeId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [email, setEmail] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [gender, setGender] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [customFieldDefs, setCustomFieldDefs] = useState<any[]>([]);
  const [pendingCustomValues, setPendingCustomValues] = useState<Record<number, any>>({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: types }, { data: orgs }, { data: gens }, { data: configs }] =
          await Promise.all([
            axios.get('crm/contact-types'),
            axios.get('crm/organisations'),
            axios.get('crm/genders'),
            axios.get('crm/field-config?table=crm_contact'),
          ]);
        setContactTypes(Array.isArray(types) ? types : []);
        setOrganisations(Array.isArray(orgs) ? orgs : orgs.data ?? []);
        setGenders(Array.isArray(gens) ? gens : []);
        setFieldConfigs(Array.isArray(configs) ? configs : []);
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    if (open && editContact) {
      setContactTypeId(editContact.contactTypeId ? String(editContact.contactTypeId) : '');
      setFirstName(editContact.firstName ?? '');
      setLastName(editContact.lastName ?? '');
      setMiddleName(editContact.middleName ?? '');
      setOrganisationId(editContact.organisationId ? String(editContact.organisationId) : '');
      setEmail(editContact.email ?? '');
      setPhone1(editContact.phone1 ?? '');
      setPhone2(editContact.phone2 ?? '');
      setJobTitle(editContact.jobTitle ?? '');
      setDepartment(editContact.department ?? '');
      setGender(editContact.gender ?? '');
      setDateOfBirth(editContact.dateOfBirth ? editContact.dateOfBirth.slice(0, 10) : '');
      setAddressLine1(editContact.addressLine1 ?? '');
      setAddressLine2(editContact.addressLine2 ?? '');
      setCity(editContact.city ?? '');
      setPostcode(editContact.postcode ?? '');
      setNotes(editContact.notes ?? '');
      setIsActive(editContact.isActive ?? true);
    } else if (open && !editContact) {
      setContactTypeId('');
      setFirstName(''); setLastName(''); setMiddleName('');
      setOrganisationId('');
      setEmail(''); setPhone1(''); setPhone2('');
      setJobTitle(''); setDepartment('');
      setGender(''); setDateOfBirth('');
      setAddressLine1(''); setAddressLine2(''); setCity(''); setPostcode('');
      setNotes('');
      setIsActive(true);
    }
  }, [open, editContact]);

  useEffect(() => {
    if (!open || !contactTypeId) {
      setCustomFieldDefs([]);
      setPendingCustomValues({});
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`fields/custom?table=crm_contact&typeId=${contactTypeId}`);
        const visible = (Array.isArray(data) ? data : []).filter((f: any) => f.isShown);
        setCustomFieldDefs(visible);
      } catch {
        setCustomFieldDefs([]);
      }
    })();
    setPendingCustomValues({});
  }, [open, contactTypeId]);

  const isSystemFieldVisible = useCallback((fieldKey: string) => {
    const typeIdNum = contactTypeId ? Number(contactTypeId) : null;
    const specific = fieldConfigs.find((c) => c.fieldKey === fieldKey && c.typeId === typeIdNum);
    const global = fieldConfigs.find((c) => c.fieldKey === fieldKey && c.typeId === null);
    const cfg = specific ?? global;
    return cfg ? cfg.isVisible : true;
  }, [fieldConfigs, contactTypeId]);

  const isSystemFieldRequired = useCallback((fieldKey: string) => {
    const typeIdNum = contactTypeId ? Number(contactTypeId) : null;
    const specific = fieldConfigs.find((c) => c.fieldKey === fieldKey && c.typeId === typeIdNum);
    const global = fieldConfigs.find((c) => c.fieldKey === fieldKey && c.typeId === null);
    const cfg = specific ?? global;
    return cfg ? cfg.isRequired : false;
  }, [fieldConfigs, contactTypeId]);

  const handleSave = async () => {
    if (!contactTypeId) {
      enqueueSnackbar('Contact type is required', { variant: 'error' });
      return;
    }

    // Validate required fields
    const missing: string[] = [];
    if (!firstName.trim()) missing.push(t('FirstName'));

    const sysChecks: [string, string, string][] = [
      ['lastName',       t('LastName'),    lastName],
      ['middleName',     t('MiddleName'),  middleName],
      ['organisationId', t('Organisations'), organisationId],
      ['email',          t('Email'),       email],
      ['phone1',         t('MobilePhone'), phone1],
      ['phone2',         t('WorkPhone'),   phone2],
      ['jobTitle',       t('JobTitle'),    jobTitle],
      ['department',     t('Department'),  department],
      ['gender',         t('Gender'),      gender],
      ['dateOfBirth',    t('DateOfBirth'), dateOfBirth],
      ['notes',          t('Description'), notes],
    ];
    for (const [key, label, val] of sysChecks) {
      if (isSystemFieldRequired(key) && isSystemFieldVisible(key) && !val.trim()) {
        missing.push(label);
      }
    }
    for (const field of customFieldDefs) {
      if (!field.isRequired) continue;
      const val = pendingCustomValues[field.id];
      const empty = val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
      if (empty) missing.push(field.column_name);
    }
    if (missing.length > 0) {
      enqueueSnackbar(`${t('Required')}: ${missing.join(', ')}`, { variant: 'error' });
      return;
    }
    const payload: any = {
      firstName: firstName.trim(),
      lastName: lastName.trim() || null,
      middleName: middleName.trim() || null,
      contactTypeId: Number(contactTypeId),
      organisationId: organisationId ? Number(organisationId) : null,
      email: email.trim() || null,
      phone1: phone1.trim() || null,
      phone2: phone2.trim() || null,
      jobTitle: jobTitle.trim() || null,
      department: department.trim() || null,
      gender: gender || null,
      dateOfBirth: dateOfBirth || null,
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      postcode: postcode.trim() || null,
      notes: notes.trim() || null,
      isActive,
    };
    try {
      if (editContact?.id) {
        await axios.put(`crm/contacts/${editContact.id}`, payload);
        enqueueSnackbar('Contact updated', { variant: 'success' });
      } else {
        const { data: created } = await axios.post('crm/contacts', payload);
        const newId = created?.id;
        if (newId && Object.keys(pendingCustomValues).length > 0) {
          await Promise.allSettled(
            Object.entries(pendingCustomValues).map(([fieldId, value]) =>
              axios.post('custom-field-values', { custom_field_id: Number(fieldId), entity_id: newId, value }),
            ),
          );
        }
        enqueueSnackbar('Contact created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const inp = (label: string, value: string, set: (v: string) => void, type = 'text', required = false) => (
    <div className="flex flex-col mb-[8px]">
      <label className="text-[10px] ml-[5px] text-[var(--text-color)]">
        {label}{required && <span className="text-red-400 ml-[2px]">*</span>}
      </label>
      <input
        title={label} placeholder={label}
        className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
        type={type} value={value} onChange={(e) => set(e.target.value)}
      />
    </div>
  );

  const sel = (label: string, value: string, set: (v: string) => void, children: React.ReactNode, required = false) => (
    <div className="flex flex-col mb-[8px]">
      <label className="text-[10px] ml-[5px] text-[var(--text-color)]">
        {label}{required && <span className="text-red-400 ml-[2px]">*</span>}
      </label>
      <select title={label} className="edit-select p-[5px] rounded-[5px] edit-input text-[14px]"
        value={value} onChange={(e) => set(e.target.value)}>
        {children}
      </select>
    </div>
  );

  const selectedType = contactTypes.find((ct) => String(ct.id) === contactTypeId);

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      width={500}
      paperStyle={{ maxWidth: '100vw', display: 'flex', flexDirection: 'column' }}
    >

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-[20px] py-[13px] flex-shrink-0"
        style={{ borderBottom: 'var(--border-input)' }}>
        <span className="text-[16px] font-semibold text-[var(--text-color)]">
          {editContact ? t('Edit') : t('AddContact')}
        </span>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--text-color)', opacity: 0.6 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto px-[20px] py-[4px] pb-[16px]">

        {/* Block 1: Contact Type */}
        <Section label={t('ContactType')}>
          <div className="flex items-center gap-[8px] mb-[8px]">
            {selectedType && (
              <span className="w-[28px] h-[28px] rounded-full flex-shrink-0 flex items-center justify-center"
                style={{ backgroundColor: selectedType.color ?? '#6366f1' }}>
                {renderIcon(selectedType.icon ?? 'person', { fontSize: 14, color: '#fff' })}
              </span>
            )}
            <select
              title={t('ContactType')}
              className="flex-1 edit-select p-[5px] rounded-[5px] edit-input text-[14px]"
              value={contactTypeId}
              onChange={(e) => setContactTypeId(e.target.value)}>
              <option value="">— {t('SelectCategory')} —</option>
              {contactTypes.map((ct) => (
                <option key={ct.id} value={ct.id}>{ct.name}</option>
              ))}
            </select>
          </div>
        </Section>

        {/* Block 2: Personal */}
        <Section label={t('Personal') || 'Personal'}>
          <div className="grid grid-cols-2 gap-x-[10px]">
            {inp(t('FirstName'), firstName, setFirstName, 'text', true)}
            {isSystemFieldVisible('lastName') ? inp(t('LastName'), lastName, setLastName, 'text', isSystemFieldRequired('lastName')) : <div />}
          </div>
          {isSystemFieldVisible('middleName') && inp(t('MiddleName'), middleName, setMiddleName, 'text', isSystemFieldRequired('middleName'))}
          {isSystemFieldVisible('gender') && sel(t('Gender'), gender, setGender,
            <>
              <option value="">—</option>
              {genders.map((g) => (
                <option key={g.id} value={g.value}>{genderLabel(g)}</option>
              ))}
            </>,
            isSystemFieldRequired('gender'),
          )}
          {isSystemFieldVisible('dateOfBirth') && inp(t('DateOfBirth'), dateOfBirth, setDateOfBirth, 'date', isSystemFieldRequired('dateOfBirth'))}
        </Section>

        {/* Block 3: Professional */}
        <Section label={t('Professional') || 'Professional'}>
          {isSystemFieldVisible('organisationId') && sel(t('Organisations'), organisationId, setOrganisationId,
            <>
              <option value="">—</option>
              {organisations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </>,
            isSystemFieldRequired('organisationId'),
          )}
          {isSystemFieldVisible('jobTitle') && inp(t('JobTitle'), jobTitle, setJobTitle, 'text', isSystemFieldRequired('jobTitle'))}
          {isSystemFieldVisible('department') && inp(t('Department'), department, setDepartment, 'text', isSystemFieldRequired('department'))}
        </Section>

        {/* Block 4: Contact Info */}
        <Section label={t('ContactInfo') || 'Contact Info'}>
          {isSystemFieldVisible('email') && inp(t('Email'), email, setEmail, 'email', isSystemFieldRequired('email'))}
          <div className="grid grid-cols-2 gap-x-[10px]">
            {isSystemFieldVisible('phone1') ? inp(t('MobilePhone'), phone1, setPhone1, 'text', isSystemFieldRequired('phone1')) : <div />}
            {isSystemFieldVisible('phone2') ? inp(t('WorkPhone'), phone2, setPhone2, 'text', isSystemFieldRequired('phone2')) : <div />}
          </div>
        </Section>

        {/* Block 5: Address */}
        <Section label={t('Address') || 'Address'}>
          <div className="grid grid-cols-2 gap-x-[10px]">
            {inp(t('AddressLine1'), addressLine1, setAddressLine1)}
            {inp(t('AddressLine2'), addressLine2, setAddressLine2)}
          </div>
          <div className="grid grid-cols-2 gap-x-[10px]">
            {inp(t('City'), city, setCity)}
            {inp(t('Postcode'), postcode, setPostcode)}
          </div>
        </Section>

        {/* Block 6: Notes */}
        {isSystemFieldVisible('notes') && (
          <Section label={<>{t('Description')}{isSystemFieldRequired('notes') && <span className="text-red-400 ml-[2px]">*</span>}</>}>
            <textarea
              title={t('Description')}
              className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mb-[8px]"
              rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            />
          </Section>
        )}

        {/* Block 7: Custom Fields — shown only when fields exist for the selected type */}
        {contactTypeId && customFieldDefs.length > 0 && (
          <Section label={t('CustomFields') || 'Custom Fields'}>
            <CustomFieldValues
              tableName="crm_contact"
              entityId={editContact?.id}
              typeId={Number(contactTypeId)}
              onValuesChange={setPendingCustomValues}
            />
          </Section>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex-shrink-0 px-[20px] py-[14px]" style={{ borderTop: 'var(--border-input)' }}>
        <label className="flex items-center gap-[8px] text-[13px] cursor-pointer mb-[12px] text-[var(--text-color)]">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          {t('IsActive')}
        </label>
        <div className="flex gap-[10px]">
          <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={handleSave}>
            {editContact ? t('Change') : t('Add')}
          </Button>
          <Button variant="outlined" sx={{ flex: 1, fontSize: 13, color: 'var(--text-color)', borderColor: 'var(--border-input)' }} onClick={onClose}>
            {t('Cancel')}
          </Button>
        </div>
      </div>
    </SidePanel>
  );
}
